import { useCallback, useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SectionCard, Field, Skeleton } from '../pipeline/ui';

// God-panel referral config: edit referral_credits + per-referrer cap (via wallet_config) and show
// a read-only granted/pending summary. Additive mount; no hardcoded numbers.
export default function ReferralAdmin() {
  const { t } = useTranslation();
  const [credits, setCredits] = useState('');
  const [cap, setCap] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.tenantAdmin('get_wallet_config').then((r) => {
      setCredits(r.config?.referral_credits ?? 0);
      setCap(r.config?.referral_cap_per_referrer ?? '');
    }).catch(() => {}).finally(() => setLoading(false));
    api.tenantAdmin('referral_summary').then(setSummary).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.tenantAdmin('set_referral_config', {
        referral_credits: Math.max(0, Math.floor(Number(credits) || 0)),
        referral_cap_per_referrer: cap === '' ? null : Math.max(0, Math.floor(Number(cap))),
      });
      toast.success(t('referral.configSaved'));
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-40 w-full" />;

  return (
    <SectionCard title={t('referral.adminTitle')} testId="referral-admin-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="referral.adminHint" /></p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={<T k="referral.creditsLabel" />}>
          <Input type="number" value={credits} onChange={(e) => setCredits(e.target.value)} data-testid="referral-credits" className="w-32" />
        </Field>
        <Field label={<T k="referral.capLabel" />}>
          <Input type="number" value={cap} onChange={(e) => setCap(e.target.value)} placeholder={t('referral.capPlaceholder')} data-testid="referral-cap" className="w-40" />
        </Field>
        <Button onClick={save} disabled={saving} data-testid="referral-config-save">{saving ? t('referral.saving') : t('referral.configSave')}</Button>
      </div>
      <div className="mt-4 text-sm text-muted-foreground font-data" data-testid="referral-summary">
        {t('referral.grantedCount')}: <span className="font-semibold">{summary?.granted ?? 0}</span>
        <span className="mx-2 text-muted-foreground">·</span>
        {t('referral.pendingCount')}: <span className="font-semibold">{summary?.pending ?? 0}</span>
      </div>
    </SectionCard>
  );
}
